/**
 * Customers: list, segments, toolbar, import and the customer form.
 * Conventions: see `common.ts`.
 */
const customers: Record<string, string> = {
  title: 'Customers',
  add_customer: 'Add customer',
  tag_placeholder: 'wholesale',

  'list.on_your_list': '{count} on your list',
  'list.loading': 'Loading your customers…',
  'list.exporting': 'Exporting…',
  'list.bulk_failed': 'Could not update those customers.',
  'list.empty_search_title': 'No customers match that search',
  'list.empty_title': 'No customers here yet',
  'list.empty_search_description': 'Try a different name, email address or phone number.',
  'list.empty_description':
    'A customer record is created the first time someone checks out - or add one yourself.',
  'list.empty_segment_description': 'Nobody matches this segment yet. Try another one.',
  'list.select_all': 'Select all customers on this page',
  'list.select_customer': 'Select {name}',

  'columns.name': 'Customer name',
  'columns.subscription': 'Email subscription',
  'columns.location': 'Location',
  'columns.orders': 'Orders',
  'columns.spent': 'Amount spent',
  'columns.phone': 'Phone',
  'columns.tags': 'Tags',
  'columns.last_order': 'Last order',

  'subscription.subscribed': 'Subscribed',
  'subscription.not_subscribed': 'Not subscribed',

  'sort.newest': 'Newest first',
  'sort.oldest': 'Oldest first',
  'sort.name_asc': 'Name A–Z',
  'sort.name_desc': 'Name Z–A',
  'sort.orders_desc': 'Most orders',
  'sort.spent_desc': 'Highest spend',
  'sort.last_order': 'Last order',

  'segments.all.label': 'All customers',
  'segments.all.description': 'Everyone on your list.',
  'segments.subscribed.label': 'Subscribed to email',
  'segments.subscribed.description':
    'Agreed to marketing email. These are the people a campaign can reach.',
  'segments.unsubscribed.label': 'Not subscribed',
  'segments.unsubscribed.description': 'Have not agreed to marketing email. Ask before you add them.',
  'segments.buyers.label': 'Has placed an order',
  'segments.buyers.description': 'Customers with at least one order behind them.',
  'segments.never_ordered.label': 'Never ordered',
  'segments.never_ordered.description': 'On your list, but yet to buy anything.',

  'sort.orders_asc': 'Fewest orders',
  'sort.spent_asc': 'Lowest spend',
  'sort.last_order_oldest': 'Longest since ordering',

  'filters.subscription': 'Marketing consent',
  'filters.orders_unit': 'orders',
  'filters.lifetime_spend': 'Lifetime spend',
  'filters.joined': 'Joined',
  'filters.any_tag': 'Any tag',

  'kpi.total': 'Customers',
  'kpi.subscribed': 'Can be emailed',
  'kpi.subscribed_hint': 'Opted in to marketing',
  'kpi.buyers': 'Have ordered',
  'kpi.buyers_hint': '{count} orders each on average',
  'kpi.average_spend': 'Average spend',
  'kpi.spend_hint': '{amount} in total',
  'kpi.new': 'New',
  'kpi.new_hint': 'Joined in the last 30 days',

  'toast.subscribed_one': '{count} customer subscribed.',
  'toast.subscribed_other': '{count} customers subscribed.',
  'toast.unsubscribed_one': '{count} customer unsubscribed.',
  'toast.unsubscribed_other': '{count} customers unsubscribed.',
  'toast.tagged_one': 'Tagged {count} customer as {tag}.',
  'toast.tagged_other': 'Tagged {count} customers as {tag}.',

  'export.csv': 'Customers (CSV)',
  'export.done': 'Your customer export is downloading.',

  'toolbar.search_label': 'Search customers',
  'toolbar.search_placeholder': 'Search by name, email or phone',
  'toolbar.subscribe': 'Subscribe',
  'toolbar.unsubscribe': 'Unsubscribe',
  'toolbar.cannot_edit': 'You cannot edit customers.',
  'toolbar.add_tag': 'Add tag',
  'toolbar.tag': 'Tag',
  'toolbar.add_to_selected': 'Add to selected',
  'toolbar.sort_by': 'Sort by',
  'toolbar.choose_columns': 'Choose columns',
  'toolbar.columns': 'Columns',
  'toolbar.reset_columns': 'Reset to default',

  'import.title': 'Import customers',
  'import.summary_new': '{count} new',
  'import.summary_updated': '{count} updated',
  'import.summary_skipped': '{count} skipped',
  'import.row_error': 'Row {row}:',
  'import.finished': 'Import finished: {summary}.',
  'import.drop_prompt': 'Drop a CSV here, or click to choose a file',
  'import.file_label': 'Customer CSV file',
  'import.help':
    'One row per customer. Rows update people you already have by email address; anyone else is added. ' +
    'Order counts and spend are exported for reference and ignored on the way back in.',
  'import.download_sample': 'Download a sample CSV',
  'import.read_failed': 'Could not read that file.',
  'import.checking': 'Checking the file…',
  'import.preview_one': 'This file has {count} row: {summary}. Nothing has been saved yet.',
  'import.preview_other': 'This file has {count} rows: {summary}. Nothing has been saved yet.',
  'import.importing': 'Importing…',

  'form.back': 'Back to customers',
  'form.edit_title': 'Edit customer',
  'form.new_title': 'New customer',
  'form.stats_one': '{count} order · {spent} spent · last order {last_order}',
  'form.stats_other': '{count} orders · {spent} spent · last order {last_order}',
  'form.load_failed': 'Could not load that customer.',
  'form.save_failed': 'Could not save. Please try again.',
  'form.save_customer': 'Save customer',
  'form.sections.overview': 'Customer overview',
  'form.sections.address': 'Default address',
  'form.sections.notes': 'Notes',
  'form.sections.tags': 'Tags',
  'form.sections.subscription': 'Email subscription',
  'form.fields.first_name': 'First name',
  'form.fields.last_name': 'Last name',
  'form.fields.email': 'Email',
  'form.fields.phone': 'Phone number',
  'form.fields.address': 'Address',
  'form.fields.address2': 'Apartment, suite, etc. (optional)',
  'form.fields.city': 'City',
  'form.fields.state': 'State',
  'form.fields.postal_code': 'PIN code',
  'form.fields.notes': 'Notes',
  'form.fields.tags': 'Tags',
  'form.email_locked_hint': 'The email address identifies this customer and cannot be changed.',
  'form.marketing_consent': 'Customer agreed to receive marketing emails and WhatsApp messages.',
  'form.marketing_consent_note':
    'Ask your customers for permission before you subscribe them. Campaigns skip anyone who has not agreed.',
  'form.address_edit_description':
    'Saved delivery addresses. The default one is prefilled at checkout and shown in your customer list. Changes here save straight away.',
  'form.address_new_description': 'The primary address of this customer.',
  'form.no_address':
    'No saved addresses yet. Add one here, or one is saved the first time this customer checks out.',
  'form.remove_address': 'Remove address',
  'form.add_address': 'Add address',
  'form.notes_description': "Notes are private and won't be shared with the customer.",
  'form.notes_placeholder': 'Prefers deliveries after 6pm.',
  'form.tags_description': 'Group customers so you can segment them later.',
  'form.sections.addresses': 'Addresses',
  'address.default': 'Default',
  'address.make_default': 'Make default',
  'address.new_title': 'Add address',
  'address.edit_title': 'Edit address',
  'address.save': 'Save address',
  'address.set_default': 'Make this the default address',
  'address.fields.name': 'Recipient name',
  'address.fields.phone': 'Recipient phone',
  'address.saved': 'Address saved.',
  'address.deleted': 'Address deleted.',
  'address.default_updated': 'Default address updated.',
  'address.save_failed': 'Could not save the address. Please try again.',
  'address.load_failed': 'Could not load the addresses.',
  'address.delete_title': 'Delete this address?',
  'address.delete_message': "{address} will be removed from this customer's address book.",
  'address.delete_default_message':
    "{address} will be removed from this customer's address book, and the next address becomes the default.",

  'validation.first_name_required': 'Enter a first name.',
  'validation.email_required': 'Enter an email address.',
  'validation.email_invalid': 'Enter a valid email address.',
  'validation.phone_invalid': 'Enter a valid Indian mobile number.',
  'validation.line1_required': 'Enter the street address.',
  'validation.city_required': 'Enter the city.',
  'validation.state_required': 'Enter the state.',
  'validation.postal_code_invalid': 'Enter a valid 6-digit PIN code.',
};

export default customers;
